/** @jsxImportSource @emotion/react */
import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  ReactNode,
} from 'react'
import { App } from 'antd'
import req from '@sportninja/common/api/request'
import { parsePhoneNumberFromString } from 'libphonenumber-js'

// Types
export type OfficialData = {
  id?: string
  name_first?: string
  name_last?: string
  email?: string
  phone_number?: string
  phone_number_country?: string
  official_type_id?: number
  official_type?: number
  is_invited?: boolean
  is_accepted?: boolean
  invitation_id?: string
}

type CustomError = {
  name_first: string | null
  name_last: string | null
  official_type_id: string | null
  email: string | null
  phone_number: string | null
  message: string | null
}

type OfficialState = {
  // Form fields
  id: string | null
  name_first: string
  name_last: string
  official_type_id: number | null
  email: string
  phone_number: string
  phone_number_country: string
  message: string

  // UI state
  isLoading: boolean
  error: string | null
  fieldErrors: string[]
  customError: CustomError
  hasInitialEmail: boolean
}

type OfficialAction =
  | { type: 'SET_FIELD'; field: keyof OfficialState; value: unknown }
  | { type: 'SET_LOADING'; value: boolean }
  | { type: 'SET_ERROR'; error: string | null }
  | { type: 'SET_FIELD_ERRORS'; errors: string[] }
  | { type: 'SET_CUSTOM_ERROR_BY_FIELD'; field: string; message: string | null }
  | { type: 'CLEAR_CUSTOM_ERROR_BY_FIELD'; field: string }
  | { type: 'CLEAR_ALL_ERRORS' }
  | { type: 'RESET_FORM' }
  | { type: 'INIT_EDIT_MODE'; data: OfficialData }

const initialCustomError: CustomError = {
  name_first: null,
  name_last: null,
  official_type_id: null,
  email: null,
  phone_number: null,
  message: null,
}

const initialState: OfficialState = {
  id: null,
  name_first: '',
  name_last: '',
  official_type_id: null,
  email: '',
  phone_number: '',
  phone_number_country: 'US',
  message: '',
  isLoading: false,
  error: null,
  fieldErrors: [],
  customError: initialCustomError,
  hasInitialEmail: false,
}

function officialReducer(
  state: OfficialState,
  action: OfficialAction
): OfficialState {
  switch (action.type) {
    case 'SET_FIELD':
      return { ...state, [action.field]: action.value }

    case 'SET_LOADING':
      return { ...state, isLoading: action.value }

    case 'SET_ERROR':
      return { ...state, error: action.error }

    case 'SET_FIELD_ERRORS':
      return { ...state, fieldErrors: action.errors }

    case 'SET_CUSTOM_ERROR_BY_FIELD':
      return {
        ...state,
        customError: {
          ...state.customError,
          [action.field]: action.message,
        },
        fieldErrors: action.message
          ? [...new Set([...state.fieldErrors, action.field])]
          : state.fieldErrors,
      }

    case 'CLEAR_CUSTOM_ERROR_BY_FIELD':
      return {
        ...state,
        customError: {
          ...state.customError,
          [action.field]: null,
        },
        fieldErrors: state.fieldErrors.filter((e) => e !== action.field),
      }

    case 'CLEAR_ALL_ERRORS':
      return {
        ...state,
        error: null,
        fieldErrors: [],
        customError: initialCustomError,
      }

    case 'RESET_FORM':
      return { ...initialState }

    case 'INIT_EDIT_MODE':
      return {
        ...state,
        id: action.data.id || null,
        name_first: action.data.name_first || '',
        name_last: action.data.name_last || '',
        official_type_id:
          action.data.official_type_id || action.data.official_type || null,
        email: action.data.email || '',
        phone_number: action.data.phone_number || '',
        phone_number_country:
          action.data.phone_number_country ||
          (action.data.phone_number
            ? parsePhoneNumberFromString(action.data.phone_number)?.country ||
              'US'
            : 'US'),
        message: '',
        hasInitialEmail: Boolean(action.data.email),
      }

    default:
      return state
  }
}

// Context type
type OfficialContextValue = {
  state: OfficialState
  dispatch: React.Dispatch<OfficialAction>
  isEdit: boolean
  onSubmit: () => Promise<void>
  onCancel: () => void
  onDelete: () => Promise<void>
  setCustomErrorByField: (field: string, message: string | null) => void
  clearCustomErrorByField: (field: string) => void
  entityType: string
  entityId: string
  isSoccer: boolean
  isOrg: boolean
  sportId: string | null
}

const OfficialContext = createContext<OfficialContextValue | null>(null)

// Hook to use official context
export const useOfficialContext = () => {
  const context = useContext(OfficialContext)
  if (!context) {
    throw new Error('useOfficialContext must be used within OfficialProvider')
  }
  return context
}

// Provider props
type OfficialProviderProps = {
  children: ReactNode
  isOpen: boolean
  onClose: () => void
  initialData?: OfficialData
  entityType: string // 'organizations' or 'schedules'
  entityId: string
  onSuccess: () => void
  isSoccer?: boolean
  isOrg?: boolean
  sportId?: string | null
}

export const OfficialProvider: React.FC<OfficialProviderProps> = ({
  children,
  onClose,
  initialData,
  entityType,
  entityId,
  onSuccess,
  isSoccer = false,
  isOrg = false,
  sportId = null,
}) => {
  const [state, dispatch] = useReducer(officialReducer, initialState)
  const { message } = App.useApp()
  const isEdit = Boolean(initialData?.id)

  // Helper functions for error handling
  const setCustomErrorByField = useCallback(
    (field: string, errorMessage: string | null) => {
      dispatch({
        type: 'SET_CUSTOM_ERROR_BY_FIELD',
        field,
        message: errorMessage,
      })
    },
    []
  )

  const clearCustomErrorByField = useCallback((field: string) => {
    dispatch({ type: 'CLEAR_CUSTOM_ERROR_BY_FIELD', field })
  }, [])

  const setCustomErrors = useCallback(
    (invalid_fields: Record<string, string | string[]>) => {
      const normalizeError = (error: string | string[]): string => {
        return Array.isArray(error) ? error.join(', ') : error
      }

      Object.entries(invalid_fields).forEach(([field, error]) => {
        setCustomErrorByField(field, normalizeError(error))
      })
    },
    [setCustomErrorByField]
  )

  // Initialize edit mode when data is available
  React.useEffect(() => {
    if (initialData && isEdit) {
      dispatch({
        type: 'INIT_EDIT_MODE',
        data: initialData,
      })
    }
  }, [initialData, isEdit])

  const onSubmit = useCallback(async () => {
    dispatch({ type: 'CLEAR_ALL_ERRORS' })

    // Front-end validation for required fields
    let hasErrors = false
    if (!state.name_first.trim()) {
      dispatch({
        type: 'SET_CUSTOM_ERROR_BY_FIELD',
        field: 'name_first',
        message: 'First name is required',
      })
      hasErrors = true
    }
    if (!state.name_last.trim()) {
      dispatch({
        type: 'SET_CUSTOM_ERROR_BY_FIELD',
        field: 'name_last',
        message: 'Last name is required',
      })
      hasErrors = true
    }

    if (hasErrors) {
      return
    }

    dispatch({ type: 'SET_LOADING', value: true })

    try {
      // Prepare payload
      const payload: Record<string, unknown> = {
        name_first: state.name_first.trim(),
        name_last: state.name_last.trim(),
        official_type_id: Number(state.official_type_id),
      }
      // Add optional fields
      if (state.email) {
        payload.email = state.email.trim()
      }
      if (state.phone_number) {
        payload.phone_number = state.phone_number
        payload.phone_number_country = state.phone_number_country || 'US'
      }
      if (state.message && !isEdit) {
        payload.message = state.message.trim()
      }

      if (isEdit && state.id) {
        // Update existing official
        await req(`/${entityType}/${entityId}/officials/${state.id}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        })
        message.success(
          `${state.name_first} ${state.name_last} updated successfully`
        )
      } else {
        // Create new official
        await req(`/${entityType}/${entityId}/officials`, {
          method: 'POST',
          body: JSON.stringify(payload),
        })
        message.success(
          `${state.name_first} ${state.name_last} created successfully`
        )
      }

      onSuccess()
      onClose()
    } catch (error: unknown) {
      const err = error as {
        invalid_fields?: Record<string, string | string[]>
        message?: string
      }

      if (err.invalid_fields) {
        setCustomErrors(err.invalid_fields)
      } else {
        const errorMessage =
          err.message ||
          (error instanceof Error ? error.message : 'An error occurred')
        dispatch({ type: 'SET_ERROR', error: errorMessage })
      }
    } finally {
      dispatch({ type: 'SET_LOADING', value: false })
    }
  }, [
    state,
    isEdit,
    entityType,
    entityId,
    onSuccess,
    onClose,
    message,
    setCustomErrors,
    setCustomErrorByField,
  ])

  const onDelete = useCallback(async () => {
    if (!isEdit || !state.id) {
      return
    }

    dispatch({ type: 'SET_LOADING', value: true })

    try {
      await req(`/${entityType}/${entityId}/officials/${state.id}`, {
        method: 'DELETE',
      })
      message.success(
        `${state.name_first} ${state.name_last} deleted successfully`
      )
      onSuccess()
      onClose()
    } catch (error: unknown) {
      const err = error as { message?: string }
      const errorMessage =
        err.message ||
        (error instanceof Error ? error.message : 'Failed to delete official')
      dispatch({ type: 'SET_ERROR', error: errorMessage })
    } finally {
      dispatch({ type: 'SET_LOADING', value: false })
    }
  }, [
    isEdit,
    state.id,
    state.name_first,
    state.name_last,
    entityType,
    entityId,
    message,
    onSuccess,
    onClose,
  ])

  const onCancel = useCallback(() => {
    dispatch({ type: 'RESET_FORM' })
    onClose()
  }, [onClose])

  const value: OfficialContextValue = {
    state,
    dispatch,
    isEdit,
    onSubmit,
    onCancel,
    onDelete,
    setCustomErrorByField,
    clearCustomErrorByField,
    entityType,
    entityId,
    isSoccer,
    isOrg,
    sportId,
  }

  return (
    <OfficialContext.Provider value={value}>
      {children}
    </OfficialContext.Provider>
  )
}

export default OfficialContext
